<template>
  <div>

    <div class="row">
      <div class="col-xl-6">
        <label class="form-label">Show completed</label>
        <div class="mt-3 mb-2">
          <bootstrap-toggle
              v-model="show_completed"
              name="show_completed"
              id="show_completed"
              :options="{ on: 'Yes', off: 'No' }"
          />
        </div>
      </div>
    </div>

    <div class="r_h_100" v-if="loading_table">
      <loadingCube :loadingMessage="'COMPANIES...'"></loadingCube>
    </div>

    <div v-else>
      <datatable
          :type="'timesheets_companies'"
          :rowData="companies"
          :loadingMessage="loadingMessage"
          :noRowsMessage="noRowsMessage"
          :columns="columns"
          :paginationSize="50"
      ></datatable>
    </div>

  </div>
</template>

<script>
import loadingCube from "loaders/record_loader";
import BootstrapToggle from 'vue-bootstrap-toggle';
import datatable from 'datatables/datatable';

export default {
  name: "all_companies",
  components: {
    loadingCube,
    BootstrapToggle,
    datatable,

    'button_show_details': {
      name: 'button_show_details',
      template: '<button type="button" class="btn btn-success" @click="show_details">Details</button>',
      methods: {
        show_details() {
          this.$store.commit('SET_ORDER_TAB_INDEX', 0);
          this.$nextTick(() => {
            const url = '/timesheets/submitted/details?company='
                + this.params.data.company_id
                + '&date='
                + this.params.data.last_submitted_date;

            this.$router.push(url);
            this.$tabs.refresh(url, true);
          });
        }
      }
    },

    'payroll_exported': {
      name: 'payroll_exported',
      template: '<div class="h3 text-center">' +
          '<span v-if="params.data.payroll_exported === 1" class="badge bg-success">YES</span>' +
          '<span v-else class="badge bg-warning">NO</span>' +
          '</div>',
    },

    'finance_exported': {
      name: 'finance_exported',
      template: '<div class="h3 text-center">' +
          '<span v-if="params.data.finance_exported === 1" class="badge bg-success">YES</span>' +
          '<span v-else class="badge bg-warning">NO</span>' +
          '</div>',
    },

    'timesheet_status': {
      name: 'timesheet_status',
      template: '<div class="h3 text-center">' +
          '<span v-if="params.data.status === \'completed\'" class="badge bg-success">COMPLETED</span>' +
          '<span v-else-if="params.data.status === \'some_already_exported\'" class="badge bg-success">SOME PAID</span>' +
          '<span v-else class="badge bg-warning">NEW</span>' +
          '</div>',
    },

    'pending_rows': {
      name: 'pending_rows',
      template: '<div class="text-center">' +
          '<span>{{ pendingRows }} / {{ totalRows }}</span>' +
          '</div>',
      computed: {
        pendingRows() {
          return this.params.data.not_completed_rows || 0;
        },
        totalRows() {
          return this.params.data.total_rows || 0;
        }
      }
    },

    'pending_note': {
      name: 'pending_note',
      template: '<div class="text-center">' +
          '<span v-if="showZeroValuePending" class="badge bg-warning">Zero value pending</span>' +
          '<span v-else-if="hasPendingMoney" class="badge bg-info">Pending value</span>' +
          '<span v-else>-</span>' +
          '</div>',
      computed: {
        totalPayAmount() {
          return parseFloat(this.params.data.total_pay_amount || 0);
        },
        pendingPayAmount() {
          return parseFloat(this.params.data.not_completed_total_pay_amount || 0);
        },
        pendingRows() {
          return parseInt(this.params.data.not_completed_rows || 0, 10);
        },
        showZeroValuePending() {
          return this.totalPayAmount > 0
              && this.pendingPayAmount === 0
              && this.pendingRows > 0;
        },
        hasPendingMoney() {
          return this.pendingPayAmount > 0;
        }
      }
    },
  },

  data() {
    return {
      companies: null,
      loadingMessage: 'Loading Companies...',
      noRowsMessage: 'No Companies Found',
      columns: [],
      show_completed: false,
      loading_table: false,
    }
  },

  beforeMount() {
    this.$bus.$on('table::reload', ($event) => {
      if ($event.table === 'all_companies' && ($event.type === 'store' || $event.type === 'update')) {
        this.get_companies();
      }
    });

    this.columns = [
      {
        headerName: 'Client name',
        field: 'company_name',
        minWidth: 150
      },
      {
        headerName: 'Last submitted date',
        field: 'last_submitted_date',
        width: 80,
        cellStyle: { 'justify-content': "center" }
      },
      {
        headerName: 'Status',
        field: 'status_string',
        width: 105,
        cellStyle: { 'justify-content': "center" },
        cellRendererFramework: 'timesheet_status'
      },
      {
        headerName: 'Cycle total',
        field: 'total_pay_amount',
        width: 80,
        cellStyle: { 'justify-content': "right" },
        valueFormatter: this.formatDecimal
      },
      {
        headerName: 'Pending value',
        field: 'not_completed_total_pay_amount',
        width: 85,
        cellStyle: { 'justify-content': "right" },
        valueFormatter: this.formatDecimal
      },
      {
        headerName: 'Exported value',
        field: 'completed_total_pay_amount',
        width: 85,
        cellStyle: { 'justify-content': "right" },
        valueFormatter: this.formatDecimal
      },
      {
        headerName: 'Pending rows',
        field: 'not_completed_rows',
        minWidth: 100,
        cellStyle: { 'justify-content': "center" },
        cellRendererFramework: 'pending_rows'
      },
      {
        headerName: 'Note',
        field: 'note',
        width: 140,
        cellStyle: { 'justify-content': "left" },
        valueGetter: this.noteValueGetter
      },
      {
        headerName: 'Payroll exported',
        field: 'payroll_exported_string',
        width: 70,
        cellStyle: { 'justify-content': "center" },
        cellRendererFramework: 'payroll_exported'
      },
      {
        headerName: 'Finance exported',
        field: 'finance_exported_string',
        width: 70,
        cellStyle: { 'justify-content': "center" },
        cellRendererFramework: 'finance_exported'
      },
      {
        headerName: 'Details',
        width: 65,
        cellStyle: { 'justify-content': "center" },
        sortable: false,
        filter: false,
        cellRendererFramework: 'button_show_details'
      },
    ];
  },

  mounted() {
    this.get_companies();
  },

  watch: {
    show_completed(newState, oldState) {
      if (oldState !== null) {
        this.get_companies(newState ? 1 : 0);
      }
    },
  },

  methods: {
    get_companies(state = null) {
      const showCompleted = state !== null
          ? state
          : (this.show_completed ? 1 : 0);

      this.loading_table = true;

      axios.get(this.crmApiPath + 'timesheet/submitted/companies', {
        params: {
          show_completed: showCompleted
        }
      }).then(response => {
        this.companies = response.data;
        this.loading_table = false;
      }).catch(error => {
        this.loading_table = false;
        console.log(error.response);
      });
    },

    formatDecimal(params) {
      const value = parseFloat(params.value || 0);

      if (isNaN(value)) {
        return '0.00';
      }

      return value
          .toFixed(2)
          .toString();
    },
  },
  noteValueGetter(params) {
    const totalPayAmount = parseFloat(params.data.total_pay_amount || 0);
    const pendingPayAmount = parseFloat(params.data.not_completed_total_pay_amount || 0);
    const pendingRows = parseInt(params.data.not_completed_rows || 0, 10);

    if (totalPayAmount > 0 && pendingPayAmount === 0 && pendingRows > 0) {
      return 'Only zero-value rows remain pending';
    }

    if (pendingPayAmount > 0) {
      return 'Payment value pending';
    }

    return '';
  },

  beforeDestroy() {
    this.companies = null;
  }
}
</script>